// pkg189 — Agent rows (D-AG6): purple Agent pill beside AI-agent actor names (desktop + mobile), Agent option in the Actor/Role filter, and an agent_denied action label (the refusals adminAuth writes for agent accounts) — delivered 2026-10-31
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';


const ACTION_LABELS = {
  login: { label: 'Login', color: '#2f5496', bg: '#f0f4ff' },
  change_password: { label: 'Password Change', color: '#555', bg: '#f5f5f5' },
  admin_login: { label: 'Admin Login', color: '#1a3a2a', bg: '#f0faf5' },
  create_client: { label: 'Create Client', color: '#1a3a2a', bg: '#f0faf5' },
  reset_client_password: { label: 'Password Reset', color: '#b7870a', bg: '#fff8e1' },
  create_billing_entry: { label: 'Billing Entry', color: '#2f5496', bg: '#f0f4ff' },
  delete_billing_entry: { label: 'Delete Entry', color: '#B85042', bg: '#fdf0ef' },
  update_hourly_rate: { label: 'Rate Update', color: '#b7870a', bg: '#fff8e1' },
  submit_demographic: { label: 'Demographic Submit', color: '#1a3a2a', bg: '#f0faf5' },
  save_parenting_plan: { label: 'Parenting Plan', color: '#1a3a2a', bg: '#f0faf5' },
  submit_witnesses: { label: 'Witness List', color: '#1a3a2a', bg: '#f0faf5' },
  submit_budget: { label: 'Budget Submit', color: '#1a3a2a', bg: '#f0faf5' },
  generate_discovery: { label: 'Discovery Generated', color: '#2f5496', bg: '#f0f4ff' },
  agent_denied: { label: 'Agent Denied', color: '#B85042', bg: '#fdf0ef' } // pkg189
};

// pkg189 — AI-agent actor marker. Same purple as the Agent role badge on
// Staff Management / Roles so the eye matches them across pages.
function AgentPill() {
  return (
    <span style={{ fontSize: 10, fontFamily: 'Arial, sans-serif', padding: '1px 6px', borderRadius: 3, marginLeft: 6,
      background: '#EEEDFE', color: '#534AB7', border: '1px solid #534AB733', fontWeight: 600, verticalAlign: 'middle' }}>
      Agent
    </span>
  );
}

function ActionBadge({ action }) {
  const a = ACTION_LABELS[action] || { label: action, color: '#555', bg: '#f5f5f5' };
  return (
    <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 7px', borderRadius: 3,
      background: a.bg, color: a.color, border: `1px solid ${a.color}33`, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {a.label}
    </span>);

}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
}

export default function AdminActivityLogPage() {

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [actor, setActor] = useState('');

  // Decide whether an actor row is a client (so we mask their name) or an
  // admin/staff user (so we leave it alone). Admin names belong to the
  // signed-in user's coworkers, not to a client, so they don't need masking
  // for tutorial privacy.
  function actorDisplay(log) {
    if (!log.actor_name) return '—';
    if (log.actor_role === 'client') return log.actor_name;
    return log.actor_name;
  }

  const fetchLogs = useCallback(async () => {
    setLoading(true);setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (from) params.from = from;
      if (to) params.to = to;
      if (actor) params.actor = actor;
      setLogs(await adminApi.getActivityLog(params));
    } catch (err) {setError(err.message);} finally
    {setLoading(false);}
  }, [search, from, to, actor]);

  useEffect(() => {fetchLogs();}, []); // load on mount

  function handleSearch(e) {
    e.preventDefault();
    fetchLogs();
  }

  const thStyle = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif',
    textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '8px 12px', fontSize: 12, fontFamily: 'Arial, sans-serif',
    borderBottom: '1px solid #eee', verticalAlign: 'top' };

  return (
    <div>
      <h1 className="page-title">Activity Log</h1>
      <p className="page-subtitle">
        Complete record of all portal activity — client submissions, admin actions, logins, and billing changes.
        Logs are retained for 90 days.
      </p>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <form onSubmit={handleSearch}>
          <div className="form-row">
            <div className="form-group" style={{ margin: 0, flex: 2 }}>
              <label>Search</label>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, action, description, IP address..." />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Actor / Role</label>
              <select value={actor} onChange={(e) => setActor(e.target.value)}>
                <option value="">All</option>
                <option value="owner">Owner</option>
                <option value="attorney">Attorney</option>
                <option value="staff">Staff</option>
                <option value="accounting">Accounting</option>
                <option value="agent">Agent</option>
                <option value="client">Client</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>From Date</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>To Date</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-sm" onClick={() => {setSearch('');setFrom('');setTo('');setActor('');}}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary btn-sm">Search</button>
          </div>
        </form>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginBottom: 8 }}>
        {loading ? 'Loading...' : `${logs.length} log entr${logs.length !== 1 ? 'ies' : 'y'} found`}
      </div>

      {/* Desktop table */}
      <div className="desktop-only table-wrap">
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date & Time</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Actor</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {!loading && logs.length === 0 &&
              <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>No log entries found.</td></tr>
              }
              {logs.map((log, i) =>
              <tr key={log.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#555' }}>{formatDateTime(log.created_at)}</td>
                  <td style={tdStyle}><ActionBadge action={log.action} /></td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>
                    {actorDisplay(log)}{log.actor_role === 'agent' && <AgentPill />}
                    {log.actor_role &&
                  <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{log.actor_role}</div>
                  }
                  </td>
                  <td style={tdStyle}>{log.description ? log.description : ''}</td>
                  <td style={{ ...tdStyle, color: '#555' }}>{log.client_name ? log.client_name : '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{log.ip_address ? log.ip_address : '—'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="mobile-only">
        {!loading && logs.length === 0 &&
        <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>No log entries found.</div>
        }
        {logs.map((log) =>
        <div key={log.id} className="card" style={{ padding: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <ActionBadge action={log.action} />
              <span style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif' }}>{formatDateTime(log.created_at)}</span>
            </div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 4 }}>{log.description ? log.description : ''}</div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888' }}>
              {log.actor_name && <span style={{ marginRight: 8 }}>{log.actor_role === 'agent' ? '🤖' : '👤'} {actorDisplay(log)}{log.actor_role === 'agent' && <AgentPill />}</span>}
              {log.client_name && <span style={{ marginRight: 8 }}>📁 {log.client_name}</span>}
              {log.ip_address && <span style={{ fontFamily: 'monospace' }}>🌐 {log.ip_address}</span>}
            </div>
          </div>
        )}
      </div>
    </div>);

}